import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import { getUserDataAndLocations, redirectToTenant, setTenantLocation } from '../actions';
import ErrorPage from '../components/error/ErrorPage';
import { getRedirectTenantInfo } from './utils';
import * as commonUtils from '../common/utils';
import {
    invitationList,
    userTenantDetailsList,
    ociErrorInfo,
    clusterLocations,
    loading,
    selectedLocation,
    userData
} from './selectors';
import LocationConfirmEditor from '../pages/location/LocationConfirmEditor';
import LocationPage from '../pages/location/LocationPage';
import {CLOUD_TYPE, DOC_CENTER_REGION_PATH, DOC_CENTER_URL, UI_MODE} from '../common/constants';
import Callback from '../components/Callback';
import TenantSelectionPage from '../components/TenantSelectionPage';
import NoCmTenantFoundPage from '../components/error/NoCmTenantFoundPage';

//Guarantee always run once before rendering
const BeforeFirstRender = ( func, params ) => {
    const willLoad = useRef(true);
    if (willLoad.current) {
        if (typeof func === 'function') {
            func(params);
        }
    }
    useEffect(() => {
        willLoad.current = false;
    });
};

export function CigServStandaloneLoadingPage({ auth, tenants, invitations, availableRegions, errorInfo, redirectToTenant, getUserDataAndLocations,
    isLoading, selectedTenantLocation, setTenantLocation}) {
    const [error, setError] = useState(false);
    const [hasInvitation, setHasInvitation] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState({});
    const [selectedLocationBox, setSelectedLocationBox ]= useState({selectedLocation: {}, confirm: false});  // Selected location tile

    BeforeFirstRender(getUserDataAndLocations, auth);

    const setSelectedCloudRegion = (region) => {
        setSelectedRegion(region);
    }

    const setSelectedLocation = (selectedBox) => {
        setSelectedLocationBox({ selectedLocation: selectedBox, confirm: true });
    }

    const confirmLocation = (location) => {
        setSelectedLocationBox({ selectedLocation: _.isNil(location) ? {} : location, confirm: false });
        setTenantLocation(location);
    }

    const cancelConfirmLocation = () => {
        setSelectedLocationBox({ selectedLocation: {}, confirm: false });
    }

    const showLocationBoxes = () => {
        return  (!error || _.isEmpty(errorInfo.errorMessage)) && _.isEmpty(tenants) &&
            (!_.isEmpty(availableRegions) && availableRegions.length > 1) && _.isEmpty(selectedTenantLocation) &&
            !commonUtils.isServiceCmUri();
    }

    const getCloudType = () => {
        let cloudType = commonUtils.getQueryStringParam(CLOUD_TYPE);
        if (_.isEmpty(cloudType)) {
            cloudType = sessionStorage.getItem(CLOUD_TYPE);
        }
        return _.trim(_.toLower(cloudType));
    }
    /**
     * Redirect to Gateway --> Redirect to Tenant
     * If the service endpoint is /service, no mode query param is added.
     * If the service endpoint is /service/cm
     *     - If mode is existed from query parameter, add mode to redirect path
     *     - If mode does not exist or empty and uiMode is not NULL, add uiMode to redirect path
     *     - If mode does not exist or empty and uiMode is empty or undefined or null, use default mode which is "CM"
     *
     * @type {(function(*, *, *): void)|*}
     */
    const redirectToTenantFn = useCallback((tenantId, hostName, uiMode) => {
        let redirectRelPath = '/web/#';
        if (commonUtils.isServiceCmUri()) {
            const mode = commonUtils.getQueryStringParam(UI_MODE.KEY);
            if (!_.isEmpty(mode) && commonUtils.validateUiMode(_.toUpper(mode))) {
                redirectRelPath = '/web/#?' + UI_MODE.KEY + '=' + _.toUpper(mode);
            }
            else if (!_.isEmpty(uiMode) && commonUtils.validateUiMode(uiMode)) {
                redirectRelPath = '/web/#?' + UI_MODE.KEY + '=' + uiMode;
            }
            else {
                // If mode is missing, default mode to CM.
                redirectRelPath = '/web/#?' + UI_MODE.KEY + '=' + UI_MODE.CM_MODE;
            }
        }

        redirectToTenant(window, auth, tenantId, hostName, redirectRelPath);
    },[auth, redirectToTenant]);

    useEffect( () => {
        const cloudType = commonUtils.getQueryStringParam(CLOUD_TYPE);
        if (!_.isEmpty(cloudType) && !_.isNil(availableRegions) && !_.isEmpty(availableRegions) && availableRegions.length > 1) {
            const cloudAvailableRegions = _.filter(availableRegions, {'cloud' : cloudType});
            if (!_.isEmpty(cloudAvailableRegions) && cloudAvailableRegions.length === 1) {
                setTenantLocation(cloudAvailableRegions[0]);
            }
        }
        else {
            if (!_.isNil(availableRegions) && !_.isEmpty(availableRegions) && availableRegions.length === 1) {
                setTenantLocation(availableRegions[0]);
            }
        }
    }, [ availableRegions, setTenantLocation ]);

    useEffect(() => {
        if (!_.isEmpty(tenants) && tenants.length > 0 && _.isEmpty(errorInfo.errorMessage)) {
            if (!commonUtils.isServiceCmUri() || tenants.length === 1) {
                const { errorStatus, tenantId, hostName, status, location, uiMode } = getRedirectTenantInfo({tenants});
                if (errorStatus) {
                    setError(errorStatus);
                }
                else if (status === 'RUNNING') {
                    // Clean tenant location lock in session storage
                    commonUtils.removeTenantLocationLock();
                    redirectToTenantFn(tenantId, hostName, uiMode);
                }
                else if (_.startsWith(status, 'CREATING')) {
                    setTenantLocation(location);
                }
            }
        }
        else if (!_.isUndefined(invitations) && invitations.length > 0 && !commonUtils.isServiceCmUri())  {
            if (invitations[0] && !commonUtils.isServiceCmUri()) {
                setHasInvitation(true);
            }
        }
    }, [ tenants, invitations, auth, setTenantLocation, redirectToTenantFn, errorInfo ]);

    const renderError = () => {
        if (error) {
            const ociSite = _.isEmpty(tenants) ? 'Unknown hostname' : _.get(tenants[0], 'hostname', 'Unknown hostname');
            return <ErrorPage id="tenant-status-error" title={'We encountered an error while accessing your Data Infrastructure Insights tenant.'}
                message={`Data Infrastructure Insights: ${ociSite}`} showCIContact={true}
                auth={auth}
                serverMsg={ociSite} logPrefix='CigServStandaloneLoadingPage:'
                data-testid="tenant-status-error"
            />
        }
        else if (!_.isEmpty(_.get(errorInfo, 'errorMessage', ''))) {
            return <ErrorPage id="error-from-server" auth={auth} message={errorInfo.errorMessage}
                showCIContact={true} serverMsg={errorInfo.errorMessage}
                errorCode={errorInfo.errorCode} httpStatusCode={errorInfo.httpStatusCode}
                logPrefix='CigServStandaloneLoadingPage:'
                data-testid="error-from-server"/>
        }
    };

    const renderCmMode = () => {
        if (!isLoading && commonUtils.isServiceCmUri() && !_.isUndefined(tenants) && _.isEmpty(errorInfo.errorMessage)) {
            if (_.isEmpty(tenants)) {
                return (
                    <>
                        <NoCmTenantFoundPage />
                    </>
                )
            }
            else if (tenants.length > 1) {
                return (
                    <>
                        <TenantSelectionPage tenants={tenants} redirectToTenantFn={redirectToTenantFn}/>
                    </>
                );
            }
        }
        return null;
    }

    const renderRedirectTenantCreation = () => {
        return (
            <Redirect id="redirect-creatingTenant" to={{
                pathname: '/createTenant',
                search: window.location.search
            }} data-testid="redirect-create-tenant"/>
        );
    }

    if (isLoading) {
        return (
            <Callback />
        );
    }

    return (
        <div id="service-loading-page" data-testid="service-loading-page">
            { renderError() }
            { renderCmMode() }
            {
                hasInvitation && (
                    <Redirect id="redirect-invite" to={{
                        pathname: '/acceptInvite',
                        search: '?context=' + invitations[0].id
                    }} data-testid="redirect-invite"/>
                )
            }
            {
                !_.isEmpty(selectedTenantLocation) && !hasInvitation && !commonUtils.isServiceCmUri() && (
                    renderRedirectTenantCreation()
                )
            }
            {
                showLocationBoxes() && <LocationPage
                    cloudHomeUrl={commonUtils.getCloudHomeUrl()}
                    selectedRegion={selectedRegion}
                    availableRegions={availableRegions}
                    setTenantCreationLocation={setSelectedLocation}  //confirm box
                    setSelectedCloudRegion={setSelectedCloudRegion}
                    helpDocumentUrl={DOC_CENTER_URL + DOC_CENTER_REGION_PATH}
                    cloudType={getCloudType()}
                    data-testid="location-page" />
            }
            {
                selectedLocationBox.confirm && <LocationConfirmEditor
                    title={'Confirm Data Infrastructure Insights Tenant Region'}
                    region={selectedLocationBox.selectedLocation}
                    cancelRegion={cancelConfirmLocation}
                    setRegion={confirmLocation} data-testid="location-confirm-box">
                </LocationConfirmEditor>
            }
        </div>
    );
}

CigServStandaloneLoadingPage.propTypes  = {
    auth: PropTypes.object,
    errorInfo: PropTypes.object,
    selectedTenantLocation: PropTypes.object,
    userData: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    invitations: PropTypes.array,
    tenants: PropTypes.array,
    availableRegions: PropTypes.array,
    getUserDataAndLocations: PropTypes.func,
    redirectToTenant: PropTypes.func.isRequired,
    getRedirectTenantInfo: PropTypes.func,
    setTenantLocation: PropTypes.func,
    removeTenantLocationLock: PropTypes.func,
};


CigServStandaloneLoadingPage.displayName = 'CigServStandaloneLoadingPage';
function mapStateToProps(state){
    return {
        invitations: invitationList(state),
        tenants: userTenantDetailsList(state),
        availableRegions: clusterLocations(state) || [],
        errorInfo: ociErrorInfo(state),
        isLoading: loading(state),
        selectedTenantLocation: selectedLocation(state),
        userData: userData(state),
    }
}
export default withRouter(
    connect(
        mapStateToProps,
        {
            getUserDataAndLocations,
            redirectToTenant,
            setTenantLocation,
        }
    )(CigServStandaloneLoadingPage));
